import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { ArtifactCard } from "src/components/ArtifactCard";
import { Sheet, SheetContent, SheetTitle } from "src/components/ui/sheet";

import { CardBox } from "../components";
import {
  extractV2TimeRange,
  getToolName,
  getToolQuestion,
  isV2ToolInput,
  processQueryData,
  processToolContent,
  processV2QueryData} from "../utils";
import { ToolCallSheet_toolCall$key } from "./__generated__/ToolCallSheet_toolCall.graphql";
import { ToolCallQueryAccordion } from "./ToolCallQueryAccordion";
import { ToolCallQuestion } from "./ToolCallQuestion";
import { ToolCallResult } from "./ToolCallResult";
import { ToolCallSheetHeader } from "./ToolCallSheetHeader";
import { ToolCallTimeRange } from "./ToolCallTimeRange";

interface ToolCallSheetProps {
  toolCall: ToolCallSheet_toolCall$key;
  index: number;
  totalCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const fragment = graphql`
  fragment ToolCallSheet_toolCall on GraphQLMessageToolCall {
    toolUse {
      id
      name
      displayName
      capabilityId
      link
      capability {
        id
        name
        parametersSchema
      }
      input {
        __typename
        ... on GenericToolInput { rawInput }
        ... on DatadogLogsToolInput { question query ddLogsStartIsoStr: startIsoStr ddLogsEndIsoStr: endIsoStr }
        ... on DatadogLogsAggregationToolInput { question query ddLogsAggStartIsoStr: startIsoStr ddLogsAggEndIsoStr: endIsoStr }
        ... on DatadogMetricsToolInput { question query ddMetricsStartIsoStr: startIsoStr ddMetricsEndIsoStr: endIsoStr }
        ... on DatadogSpansToolInput { question query ddSpansStartIsoStr: startIsoStr ddSpansEndIsoStr: endIsoStr }
        ... on DatadogSpansAggregationToolInput { question query ddSpansAggStartIsoStr: startIsoStr ddSpansAggEndIsoStr: endIsoStr }
        ... on DatadogRumAggregationToolInput { question query ddRumAggStartIsoStr: startIsoStr ddRumAggEndIsoStr: endIsoStr }
        ... on DatadogEventsToolInput { question query ddEventsStartIsoStr: startIsoStr ddEventsEndIsoStr: endIsoStr }
        ... on DatadogCIPipelineEventsToolInput { question query ddCIPipelineStartIsoStr: startIsoStr ddCIPipelineEndIsoStr: endIsoStr }
        ... on DatadogCIPipelineEventsAggregationToolInput { question query ddCIPipelineAggStartIsoStr: startIsoStr ddCIPipelineAggEndIsoStr: endIsoStr }
        ... on DatadogDashboardToolInput { question ddDashboardId: dashboardId }
        ... on DatadogNotebookToolInput { question notebookId }
        ... on DatadogMonitorToolInput { question monitorId }
        ... on DatadogServiceDependencyToolInput { question serviceName }
        ... on DatadogErrorTrackingIssueDetailsToolInput { question issueId }
        ... on DatadogErrorTrackingIssuesToolInput { question query ddErrorTrackingStartIsoStr: startIsoStr ddErrorTrackingEndIsoStr: endIsoStr persona track }
        ... on BuildkiteBuildsToolInput { question pipelineSlug queryParams buildNumberStr: buildNumber }
        ... on BuildkiteLogsToolInput { question pipelineSlug buildNumberInt: buildNumber stateFilter retryTypeFilter }
        ... on BuildkiteAnnotationsToolInput { question pipelineSlug buildNumberInt: buildNumber contextFilter }
        ... on BuildkiteArtifactsToolInput { question pipelineSlug buildNumberInt: buildNumber artifactPath searchQuery stepKey jobName }
        ... on BuildkiteFlakyTestToolInput { question testSuiteSlug testName }
        ... on CloudWatchLogsToolInput { question query cwLogsStartIsoStr: startIsoStr cwLogsEndIsoStr: endIsoStr logGroupNames }
        ... on CloudWatchLogsAggregationToolInput { question query cwLogsAggStartIsoStr: startIsoStr cwLogsAggEndIsoStr: endIsoStr logGroupNames }
        ... on CloudWatchMetricsToolInput { question query cwMetricsStartIsoStr: startIsoStr cwMetricsEndIsoStr: endIsoStr }
        ... on GrafanaCloudLogsToolInput { question query gcLogsStartIsoStr: startIsoStr gcLogsEndIsoStr: endIsoStr }
        ... on GrafanaCloudLogsAggregationToolInput { question query gcLogsAggStartIsoStr: startIsoStr gcLogsAggEndIsoStr: endIsoStr }
        ... on GrafanaCloudMetricsToolInput { question query gcMetricsStartIsoStr: startIsoStr gcMetricsEndIsoStr: endIsoStr }
        ... on GrafanaCloudSpansAggregationToolInput { question query gcSpansAggStartIsoStr: startIsoStr gcSpansAggEndIsoStr: endIsoStr }
        ... on GrafanaCloudSpansToolInput { question query gcSpansStartIsoStr: startIsoStr gcSpansEndIsoStr: endIsoStr }
        ... on NewRelicLogsToolInput { question query nrLogsStartIsoStr: startIsoStr nrLogsEndIsoStr: endIsoStr }
        ... on NewRelicLogsAggregationToolInput { question query nrLogsAggStartIsoStr: startIsoStr nrLogsAggEndIsoStr: endIsoStr }
        ... on NewRelicDashboardToolInput { question nrDashboardId: dashboardId }
        ... on NewRelicMetricsToolInput { question query nrMetricsStartIsoStr: startIsoStr nrMetricsEndIsoStr: endIsoStr }
        ... on NewRelicSpansToolInput { question query nrSpansStartIsoStr: startIsoStr nrSpansEndIsoStr: endIsoStr }
        ... on NewRelicSpansAggregationToolInput { question query nrSpansAggStartIsoStr: startIsoStr nrSpansAggEndIsoStr: endIsoStr }
        ... on NewRelicTransactionsToolInput { question query nrTransactionsStartIsoStr: startIsoStr nrTransactionsEndIsoStr: endIsoStr }
        ... on NewRelicTransactionsAggregationToolInput { question query nrTransactionsAggStartIsoStr: startIsoStr nrTransactionsAggEndIsoStr: endIsoStr }
        ... on NewRelicChangeTrackingToolInput { question nrChangeTrackingQuery: query nrChangeTrackingStartIsoStr: startIsoStr nrChangeTrackingEndIsoStr: endIsoStr }
        ... on QueryCodeToolInput { question repoName }
        ... on GitHubActionsRunsToolInput { question repoName runId }
        ... on GitHubActionsLogsToolInput { question repoName runId statusFilter conclusionFilter }
        ... on RootlyDashboardToolInput { question dashboardId dashboardSlug startIsoStr endIsoStr period }
        ... on SentryIssuesToolInput { question projectId environment sentryQueryParams: queryParams sentryIssuesStartIsoStr: startIsoStr sentryIssuesEndIsoStr: endIsoStr }
        ... on SentryIssueEventsToolInput { question issueId }
        ... on SlackChannelContentsToolInput { question channelId threadTs }
        ... on SlackChannelMetadataToolInput { question channelNameFilter slackStartIsoStr: startIsoStr slackEndIsoStr: endIsoStr }
        ... on SlackUsergroupMentionToolInput { usergroupHandle }
        ... on TemporalWorkflowsToolInput { question namespace query }
        ... on TemporalWorkflowHistoryToolInput { question namespace workflowId workflowRunId }
        ... on UpdateTodoToolInput { tasks }
        ... on SearchWebToolInput { userQuery domainFilter numResults }
        ... on FetchWebContentToolInput { urls maxCharacters }
      }
    }
    toolResult {
      content
      hasData
      commandFailed
      artifactId
      groupTags
      ephemeral
    }
  }
`;

const CapabilityParameters: React.FC<{ toolCall: any }> = ({ toolCall }) => {
  const [expandedParams, setExpandedParams] = React.useState<Set<string>>(new Set());

  const capability = toolCall.toolUse.capability;
  const capabilityGlobalId = btoa(`GraphQLCapability:${toolCall.toolUse.capabilityId}`);
  const parametersSchema = capability.parametersSchema as any;
  const rawInput = (toolCall.toolUse.input as any)?.rawInput || {};

  // Parse parameters schema
  const properties = parametersSchema?.properties || {};
  const required = new Set(parametersSchema?.required || []);

  const toggleParam = (paramName: string) => {
    setExpandedParams(prev => {
      const next = new Set(prev);
      if (next.has(paramName)) {
        next.delete(paramName);
      } else {
        next.add(paramName);
      }
      return next;
    });
  };

  return (
    <>
      <CardBox title="Capability">
        <a
          href={`/capabilities/${capabilityGlobalId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-foreground hover:text-foreground bg-muted hover:bg-card border border-border rounded px-2 py-1 transition-colors"
        >
          <ExternalLink className="h-3 w-3" />
          <span>View Capability Details</span>
        </a>
      </CardBox>

      {/* Parameters Section */}
      {Object.keys(properties).length > 0 && (
        <CardBox title="Parameters">
          <div className="space-y-1">
            {Object.entries(properties).map(([paramName, paramSchema]: [string, any]) => {
              const value = rawInput[paramName];
              const isRequired = required.has(paramName);
              const description = paramSchema.description || "";
              const type = paramSchema.type || "string";
              const enumValues = paramSchema.enum;
              const defaultValue = paramSchema.default;
              const isExpanded = expandedParams.has(paramName);
              const hasDetails = description || enumValues;

              // Format the display value
              let displayValue: string;
              if (value !== undefined) {
                displayValue = typeof value === "string" ? value : JSON.stringify(value);
              } else if (defaultValue !== undefined) {
                displayValue = `None (default: ${typeof defaultValue === "string" ? defaultValue : JSON.stringify(defaultValue)})`;
              } else {
                displayValue = "None";
              }

              return (
                <div key={paramName}>
                  <div className="flex items-center gap-1.5 text-sm py-1">
                    {hasDetails && (
                      <button
                        onClick={() => toggleParam(paramName)}
                        className="text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                    )}
                    {!hasDetails && <div className="w-3" />}
                    <span className="font-medium text-foreground min-w-[110px]">{paramName}:</span>
                    <span className="text-foreground font-mono">{displayValue}</span>
                    {isRequired && (
                      <span className="text-xs text-red-400">*</span>
                    )}
                  </div>
                  {/* Expanded details */}
                  {isExpanded && hasDetails && (
                    <div className="ml-[126px] mb-2 text-xs text-muted-foreground bg-muted rounded px-2 py-1.5 border border-border">
                      {description && <div>{description}</div>}
                      {enumValues && (
                        <div className="mt-1 text-gray-500">
                          Options: {enumValues.join(", ")}
                        </div>
                      )}
                      <div className="mt-1 text-gray-500">Type: {type}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardBox>
      )}
    </>
  );
};

export const ToolCallSheet: React.FC<ToolCallSheetProps> = ({
  index,
  totalCount,
  isOpen,
  onOpenChange,
  onNext,
  onPrevious,
  toolCall: toolCallProp
}) => {
  const data = useFragment(fragment, toolCallProp);
  const toolCall = data;

  // Extract tool name and question using utility functions
  const toolName = getToolName(toolCall);
  const question = getToolQuestion(toolCall);

  // State for navigation boundary flash effect
  const [flashFirst, setFlashFirst] = React.useState(false);
  const [flashLast, setFlashLast] = React.useState(false);

  // Process tool content using utility function
  const processedContent = React.useMemo(() =>
    processToolContent(toolCall),
    [toolCall.toolResult]
  );

  // Process query data using utility function
  const queryData = React.useMemo(() => {
    const input = toolCall.toolUse?.input;
    if (input && isV2ToolInput(input)) {
      return processV2QueryData((input as any).rawInput, toolCall.toolUse?.name || "");
    }
    return processQueryData(input, toolName);
  }, [toolCall.toolUse?.input, toolCall.toolUse?.name, toolName]);

  // Extract artifactId and hasData for display
  const artifactId = toolCall.toolResult?.artifactId;
  const groupTags = toolCall.toolResult?.groupTags;

  // Helper to extract time range fields - using aliased field names
  const getTimeRange = () => {
    const input = toolCall.toolUse?.input;
    if (!input) return { startTime: undefined, endTime: undefined };

    // Check for V2 rawInput first
    if (isV2ToolInput(input)) {
      return extractV2TimeRange((input as any).rawInput);
    }

    // Check for various aliased startIsoStr/endIsoStr fields based on tool type
    const timeFields = [
      "ddLogsStartIsoStr", "ddLogsAggStartIsoStr", "ddMetricsStartIsoStr",
      "ddSpansStartIsoStr", "ddSpansAggStartIsoStr", "ddRumAggStartIsoStr",
      "ddEventsStartIsoStr", "ddCIPipelineStartIsoStr", "ddCIPipelineAggStartIsoStr",
      "ddErrorTrackingStartIsoStr",
      "cwLogsStartIsoStr", "cwLogsAggStartIsoStr", "cwMetricsStartIsoStr",
      "gcLogsStartIsoStr", "gcLogsAggStartIsoStr", "gcMetricsStartIsoStr", "gcSpansAggStartIsoStr", "gcSpansStartIsoStr",
      "nrLogsStartIsoStr", "nrLogsAggStartIsoStr", "nrMetricsStartIsoStr",
      "nrSpansStartIsoStr", "nrSpansAggStartIsoStr", "nrTransactionsStartIsoStr",
      "nrTransactionsAggStartIsoStr", "nrChangeTrackingStartIsoStr",
      "slackStartIsoStr", "sentryIssuesStartIsoStr"
    ];

    const endTimeFields = [
      "ddLogsEndIsoStr", "ddLogsAggEndIsoStr", "ddMetricsEndIsoStr",
      "ddSpansEndIsoStr", "ddSpansAggEndIsoStr", "ddRumAggEndIsoStr",
      "ddEventsEndIsoStr", "ddCIPipelineEndIsoStr", "ddCIPipelineAggEndIsoStr",
      "ddErrorTrackingEndIsoStr",
      "cwLogsEndIsoStr", "cwLogsAggEndIsoStr", "cwMetricsEndIsoStr",
      "gcLogsEndIsoStr", "gcLogsAggEndIsoStr", "gcMetricsEndIsoStr", "gcSpansAggEndIsoStr", "gcSpansEndIsoStr",
      "nrLogsEndIsoStr", "nrLogsAggEndIsoStr", "nrMetricsEndIsoStr",
      "nrSpansEndIsoStr", "nrSpansAggEndIsoStr", "nrTransactionsEndIsoStr",
      "nrTransactionsAggEndIsoStr", "nrChangeTrackingEndIsoStr",
      "slackEndIsoStr", "sentryIssuesEndIsoStr"
    ];

    // Find the first matching field
    for (let i = 0; i < timeFields.length; i++) {
      const startField = timeFields[i];
      const endField = endTimeFields[i];
      if (startField in input && endField in input) {
        return {
          startTime: (input as any)[startField],
          endTime: (input as any)[endField]
        };
      }
    }

    return { startTime: undefined, endTime: undefined };
  };

  const { startTime, endTime } = getTimeRange();

  // Check if this is a V2 message
  const isV2 = isV2ToolInput(toolCall.toolUse?.input);

  // Helper to extract log group names
  const getLogGroupNames = () => {
    const input = toolCall.toolUse?.input;
    if (!input) return undefined;

    // Check for V2 rawInput first
    if (isV2ToolInput(input)) {
      try {
        const rawInput = typeof (input as any).rawInput === "string"
          ? JSON.parse((input as any).rawInput)
          : (input as any).rawInput;

        // CloudWatch tools have log_group_names field
        if (rawInput.log_group_names && Array.isArray(rawInput.log_group_names)) {
          return rawInput.log_group_names;
        }
      } catch (e) {
        console.error("Error parsing rawInput for log groups:", e);
      }
    }

    // Check for V1 typed input
    if (input.__typename === "CloudWatchLogsToolInput" || input.__typename === "CloudWatchLogsAggregationToolInput") {
      return input.logGroupNames;
    }
    return undefined;
  };

  const logGroupNames = getLogGroupNames();

  // Function to trigger flash animation for boundaries
  const triggerFlash = (isFirst: boolean) => {
    if (isFirst) {
      setFlashFirst(true);
      setTimeout(() => setFlashFirst(false), 300);
    } else {
      setFlashLast(true);
      setTimeout(() => setFlashLast(false), 300);
    }
  };

  // Add keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle navigation if the sheet is open
      if (!isOpen) return;

      // Prevent default behavior for arrow keys
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
      }

      // Navigate based on arrow keys
      if (event.key === "ArrowLeft") {
        if (index > 0) {
          onPrevious();
        } else {
          // Flash indicator when at first item
          triggerFlash(true);
        }
      } else if (event.key === "ArrowRight") {
        if (index < totalCount - 1) {
          onNext();
        } else {
          // Flash indicator when at last item
          triggerFlash(false);
        }
      }
    };

    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, index, totalCount, onNext, onPrevious]);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className="bg-popover text-foreground border-border w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl p-0 flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetTitle className="sr-only">{toolName} Details</SheetTitle>

        {/* Header with navigation */}
        <ToolCallSheetHeader
          toolName={toolName}
          index={index}
          totalCount={totalCount}
          hasContent={!!processedContent}
          hasData={!!toolCall.toolResult?.hasData}
          commandFailed={!!toolCall.toolResult?.commandFailed}
          onNext={onNext}
          onPrevious={onPrevious}
          onClose={() => onOpenChange(false)}
          flashFirst={flashFirst}
          flashLast={flashLast}
        />

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            {/* Question */}
            <ToolCallQuestion question={question} />

            {/* Capability Link and Parameters - Show for capability tools */}
            {toolCall.toolUse?.capability && <CapabilityParameters toolCall={toolCall} />}

            {/* Query details - hide for ephemeral tools and capability tools */}
            {/* Use "Parameters" for RunCapability, "Query" for other tools */}
            {toolCall.toolUse?.input && queryData.displayData && !toolCall.toolResult?.ephemeral && !toolCall.toolUse?.capability && (
              <CardBox
                title={toolName === "RunCapability" ? "Parameters" : "Query"}
                titleAction={
                  toolCall.toolUse?.link ? (
                    <a
                      href={toolCall.toolUse.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-foreground hover:text-foreground bg-muted hover:bg-card border border-border rounded px-2 py-1 transition-colors"
                      title={toolName === "QueryGCPCloudMonitoringLogsAggregation" ? "Copy and paste the query into BigQuery to see the evidenced data" : undefined}
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>{toolName}</span>
                    </a>
                  ) : undefined
                }
              >
                {/* Time Range and Log Groups */}
                <ToolCallTimeRange
                  startTime={startTime}
                  endTime={endTime}
                  logGroups={logGroupNames}
                />

                {/* Query Accordion */}
                <ToolCallQueryAccordion
                  displayData={queryData.displayData}
                  rawData={queryData.rawData}
                  isJson={queryData.isJson}
                />
              </CardBox>
            )}

            {/* Result - only show for V1 messages */}
            {!isV2 && (
              <ToolCallResult
                content={processedContent}
              />
            )}

            {/* Sample Data - for V2 messages, always show; for V1 only show if artifactId exists */}
            {/* Use "Results" for RunCapability, "Sample Data" for other tools */}
            {isV2 ? (
              <CardBox title={toolName === "RunCapability" ? "Results" : "Sample Data"}>
                {artifactId ? (
                  <ArtifactCard
                    artifactId={artifactId}
                    groupTags={groupTags || []}
                    title="Tool Result Data"
                    description="Data generated from the tool execution"
                    query=""
                  />
                ) : (
                  <div className="flex items-center justify-center py-6 text-gray-500 italic text-sm">
                    No Data
                  </div>
                )}
              </CardBox>
            ) : (
              artifactId && (
                <CardBox title="Data">
                  <ArtifactCard
                    artifactId={artifactId}
                    groupTags={groupTags || []}
                    title="Tool Result Data"
                    description="Data generated from the tool execution"
                    query=""
                  />
                </CardBox>
              )
            )}

            {/* Keyboard shortcut hint */}
            <div className="pt-2 text-center text-xs text-muted-foreground italic border-t border-border/50 mt-4">
              Tip: Use{" "}
              <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                ←
              </kbd>{" "}
              and{" "}
              <kbd className="px-1.5 py-0.5 bg-muted rounded border border-border">
                →
              </kbd>{" "}
              arrow keys for navigation
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
